import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import AssessmentRoundedIcon from "@mui/icons-material/AssessmentRounded";
import dayjs, { Dayjs } from "dayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useQuery } from "@tanstack/react-query";
import useAuth from "../../../hooks/useAuth";
import { fetchOperationsPlannerSummary } from "../../../api/operationsPlannerSummaryService";
import { OperationsPlannerSummaryDto } from "../../../interfaces/Operations/operationsPlannerSummary";

const formatSummaryDate = (value: Dayjs) => value.format("dddd, MMMM D");

const buildMonthDates = (month: Dayjs) => {
  const startOfMonth = month.startOf("month");
  const totalDays = startOfMonth.daysInMonth();

  return Array.from({ length: totalDays }, (_, index) =>
    startOfMonth.add(index, "day"),
  );
};

const buildAverageUtilizationReport = (
  summaries: OperationsPlannerSummaryDto[],
) => {
  const reports = summaries
    .map((item) => item.utilizationReport)
    .filter(
      (
        report,
      ): report is NonNullable<
        OperationsPlannerSummaryDto["utilizationReport"]
      > => Boolean(report),
    );

  if (!reports.length) {
    return undefined;
  }

  if (reports.length === 1) {
    return {
      ...reports[0],
      title: `Utilization for ${formatSummaryDate(dayjs(summaries[0].date))}`,
    };
  }

  const itemAccumulator = new Map<
    string,
    { label: string; numberSum: number; percentageSum: number; count: number }
  >();

  reports.forEach((report) => {
    report!.items.forEach((item) => {
      const existing = itemAccumulator.get(item.key) || {
        label: item.label,
        numberSum: 0,
        percentageSum: 0,
        count: 0,
      };

      itemAccumulator.set(item.key, {
        label: item.label,
        numberSum: existing.numberSum + item.number,
        percentageSum: existing.percentageSum + item.percentageOfActive,
        count: existing.count + 1,
      });
    });
  });

  const averagedItems = Array.from(itemAccumulator.entries()).map(
    ([key, value]) => ({
      key,
      label: value.label,
      number: Number((value.numberSum / value.count).toFixed(2)),
      percentageOfActive: Number(
        (value.percentageSum / value.count).toFixed(2),
      ),
    }),
  );

  return {
    title: `Average utilization across ${reports.length} days`,
    items: averagedItems,
  };
};

const OperationsUtilizationReport = () => {
  const { token } = useAuth();
  const [selectedDates, setSelectedDates] = useState<Dayjs[]>([
    dayjs().startOf("day"),
  ]);
  const [selectedMonth, setSelectedMonth] = useState(dayjs().startOf("month"));

  const monthDates = useMemo(
    () => buildMonthDates(selectedMonth),
    [selectedMonth],
  );

  const sortedSelectedDates = useMemo(
    () =>
      [...selectedDates].sort(
        (first, second) => first.valueOf() - second.valueOf(),
      ),
    [selectedDates],
  );

  const selectedDateKeys = useMemo(
    () => sortedSelectedDates.map((date) => date.format("YYYY-MM-DD")),
    [sortedSelectedDates],
  );

  const selectedDateRangeLabel = useMemo(() => {
    if (!sortedSelectedDates.length) {
      return "";
    }

    if (sortedSelectedDates.length === 1) {
      return formatSummaryDate(sortedSelectedDates[0]);
    }

    const start = sortedSelectedDates[0];
    const end = sortedSelectedDates[sortedSelectedDates.length - 1];

    return `${start.format("MMM D, YYYY")} - ${end.format("MMM D, YYYY")}`;
  }, [sortedSelectedDates]);

  const {
    data: summaries,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery<OperationsPlannerSummaryDto[], Error>({
    queryKey: ["operations-utilization-report", selectedDateKeys],
    queryFn: () =>
      Promise.all(
        selectedDateKeys.map((date) =>
          fetchOperationsPlannerSummary(token, date),
        ),
      ),
    enabled: Boolean(token) && selectedDateKeys.length > 0,
    placeholderData: (previousData) => previousData,
  });

  const utilizationReport = useMemo(
    () => (summaries ? buildAverageUtilizationReport(summaries) : undefined),
    [summaries],
  );

  const handleMonthChange = (value: Dayjs | null) => {
    if (!value) {
      return;
    }

    setSelectedMonth(value.startOf("month"));
  };

  const handleShiftMonth = (offset: number) => {
    setSelectedMonth((prev) => prev.add(offset, "month"));
  };

  const handleSelectFullMonth = () => {
    setSelectedDates(monthDates.map((date) => date.startOf("day")));
  };

  const handleClearSelection = () => {
    setSelectedDates([]);
  };

  const handleDayToggle = (value: Dayjs) => {
    setSelectedDates((prev) => {
      const normalized = value.startOf("day");
      const exists = prev.some((item) => item.isSame(normalized, "day"));

      if (exists) {
        if (prev.length === 1) {
          return prev;
        }

        return prev.filter((item) => !item.isSame(normalized, "day"));
      }

      return [...prev, normalized];
    });
  };

  const openAvailableTrucksInNewTab = () => {
    const url = `${window.location.origin}/fleet/assets?tab=1&status=${encodeURIComponent(
      "Available",
    )}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 0, sm: 3 }, minWidth: 0 }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ xs: "flex-start", md: "center" }}
        justifyContent="space-between"
        mb={3}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <AssessmentRoundedIcon color="primary" />
          <Box>
            <Typography variant="h4" fontWeight={600}>
              Utilization report
            </Typography>
            <Typography variant="body2" color="text.secondary">
              View utilization for one day or average metrics across multiple
              selections.
            </Typography>
          </Box>
        </Stack>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ maxWidth: "100%" }}
          justifyContent="flex-end"
        >
          <IconButton
            aria-label="Previous month"
            onClick={() => handleShiftMonth(-1)}
          >
            <ArrowBackIosNewRoundedIcon fontSize="small" />
          </IconButton>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Month"
              value={selectedMonth}
              onChange={handleMonthChange}
              views={["year", "month"]}
              format="MMMM YYYY"
              slotProps={{
                textField: {
                  size: "small",
                  sx: { width: { xs: 172, sm: 220 }, maxWidth: "100%" },
                },
              }}
            />
          </LocalizationProvider>
          <IconButton
            aria-label="Next month"
            onClick={() => handleShiftMonth(1)}
          >
            <ArrowForwardIosRoundedIcon fontSize="small" />
          </IconButton>
          <Tooltip title="Refresh">
            <span>
              <IconButton
                aria-label="Refresh report"
                onClick={() => refetch()}
                disabled={isFetching}
              >
                <RefreshRoundedIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="outlined"
            size="small"
            onClick={handleClearSelection}
          >
            Clear selection
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleSelectFullMonth}
          >
            Select full month
          </Button>
        </Stack>
      </Stack>

      {isFetching && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          mb={2}
          color="text.secondary"
        >
          <CircularProgress size={18} thickness={4} />
          <Typography variant="body2">Loading latest data...</Typography>
        </Stack>
      )}

      <Box
        className="report-date-picker"
        display="flex"
        flexWrap="wrap"
        gap={1}
        mb={3}
      >
        {monthDates.map((date) => {
          const key = date.format("YYYY-MM-DD");
          const isSelected = sortedSelectedDates.some((item) =>
            item.isSame(date, "day"),
          );

          return (
            <Button
              key={key}
              variant={isSelected ? "contained" : "outlined"}
              color={isSelected ? "primary" : "inherit"}
              onClick={() => handleDayToggle(date)}
              size="small"
              sx={{
                minWidth: 80,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 1,
              }}
            >
              <Typography variant="caption" sx={{ textTransform: "uppercase" }}>
                {date.format("ddd")}
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {date.format("D")}
              </Typography>
            </Button>
          );
        })}
      </Box>

      {isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error?.message || "Unable to load utilization report."}
        </Alert>
      )}

      {!utilizationReport ? (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          minHeight="40vh"
        >
          <Typography color="text.secondary">
            No utilization data available for the selected dates.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardHeader
                title={selectedDateRangeLabel}
                subheader={utilizationReport.title}
              />
              <CardContent>
                <Grid container spacing={3}>
                  {["utilized", "non-utilized"].map((groupKey) => {
                    const items = utilizationReport.items.filter((item) =>
                      item.key.startsWith(groupKey),
                    );

                    if (!items.length) {
                      return null;
                    }

                    const primaryItem = items.find(
                      (item) => item.key === `${groupKey}-trucks`,
                    );
                    const breakdownItems = items.filter(
                      (item) => item.key !== `${groupKey}-trucks`,
                    );

                    const title =
                      groupKey === "utilized"
                        ? "Utilized trucks"
                        : "Non utilized trucks";

                    return (
                      <Grid item xs={12} md={6} key={groupKey}>
                        <Box
                          p={2}
                          border={1}
                          borderColor="divider"
                          borderRadius={2}
                          height="100%"
                          display="flex"
                          flexDirection="column"
                          gap={2}
                        >
                          <Box>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                              textTransform="uppercase"
                              fontWeight={700}
                              gutterBottom
                            >
                              {title}
                            </Typography>
                            {primaryItem && (
                              <Stack
                                direction="row"
                                spacing={2}
                                alignItems="baseline"
                              >
                                <Typography variant="h4" fontWeight={800}>
                                  {primaryItem.number}
                                </Typography>
                                <Typography color="text.secondary">
                                  {primaryItem.percentageOfActive}% of active
                                </Typography>
                              </Stack>
                            )}
                          </Box>

                          {breakdownItems.length > 0 && (
                            <Grid container spacing={2}>
                              {breakdownItems.map((item) => (
                                <Grid item xs={12} sm={6} key={item.key}>
                                  <Box
                                    p={2}
                                    border={1}
                                    borderColor="divider"
                                    borderRadius={2}
                                  >
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      textTransform="uppercase"
                                      gutterBottom
                                      display="block"
                                      sx={
                                        item.label.toLowerCase() ===
                                        "available no driver assigned"
                                          ? {
                                              cursor: "pointer",
                                              textDecoration: "underline",
                                            }
                                          : undefined
                                      }
                                      onClick={() => {
                                        if (
                                          item.label.toLowerCase() ===
                                          "available no driver assigned"
                                        ) {
                                          openAvailableTrucksInNewTab();
                                        }
                                      }}
                                    >
                                      {item.label}
                                    </Typography>
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      alignItems="baseline"
                                    >
                                      <Typography variant="h6" fontWeight={700}>
                                        {item.number}
                                      </Typography>
                                      <Typography color="text.secondary">
                                        {item.percentageOfActive}%
                                      </Typography>
                                    </Stack>
                                  </Box>
                                </Grid>
                              ))}
                            </Grid>
                          )}
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default OperationsUtilizationReport;
